import { getCoreUtils, nz, StringOnlyOf } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAnswerDoc, AppAssistApi } from "./AppAssistApi";
import { AppTaskApi } from "./AppTaskApi";

// AppMoney - the Revenue upgrade's CONTRACT (owner request 2026-10-14 "The revenues tab needs some serious
// upgrades"): `money.json` (the owner's own decisions about money, per person), the body the page sends to
// `/money`, the reading of its answer, and the extra fields an `index.json` money row carries.
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`, NO `node:` import. The SERVICE writes `money.json`
// (`MoneyRun`) and lays it over the harvest (`MoneyOverlay`); the PAGE reads the rows. Both use THIS reader,
// THIS serialiser and THESE id shapes, so the halves cannot disagree (`MoneyContract.test.ts`).
//
//     { "schema": 1, "personKey": "+46...", "items": [ { "id": "m-<12 hex>" | "v-<12 hex>", "at": ISO-8601 UTC,
//       "state"?: "open" | "settled", "settledOn"?: "YYYY-MM-DD",
//       "amount"?: <minor units>, "currency"?: "EUR", "direction"?: "in" | "out", "due"?: "YYYY-MM-DD",
//       "add"?: { "label", "value", "amount", "currency", "direction", "due", "date" } } ] }
//
// ==================================================================================================
// !! ⛔ DORMANT UNTIL `WAC_MONEY` IS EXACTLY "1"
// ==================================================================================================
// Off: the page, `index.json`, the analysis prompt and the AI panel are byte for byte as before
// (`MoneyOff.test.ts`), and `/money` does not exist. ⭐ THE APP'S SWITCH is `state` on an `index.json` money row
// (`HasState`) - an older index paints the old Revenue list byte for byte.
//
// ⛔ THE FILE IS OUTSIDE INPUT; THE READER IS STRICT (`TaskStateReader`'s rule): one malformed item makes the
// whole document `Unreadable`, and an unreadable file is NEVER written over (the service refuses with a sentence).
// ⛔ IDS ARE NEVER ASKED OF A MODEL: `m-` + 12 hex of sha1(personKey | folded label | anchor) for a harvested
// item (`MoneyIds.AiId`, the `t-` formula), `v-` + 12 random hex for one the owner added (the page may send its
// own, so a retried press adds it once).
// ⛔ A HUMAN DECISION OR CORRECTION ALWAYS BEATS RE-ANALYSIS (`MoneyOverlay`).
// ⚠ AMOUNTS ARE MINOR UNITS (1250 = 12.50 EUR), as the harvest writes them (`DossierMoney`). ⚠ PRIVATE DATA:
// nothing here or in its callers is ever logged - counts only.

/** What the owner typed for an item of their own. */
export class AppMoneyAdd {
    public Label: string = "";
    public Value: string = "";
    /** Minor units, > 0. */
    public Amount: number = 0;
    public Currency: string = "";
    /** `in` (owed to the owner) or `out` (the owner owes). */
    public Direction: string = "";
    public Due: string = "";
    /** When it arose, `YYYY-MM-DD` ("" = the day it was added). */
    public Date: string = "";
}

/** One decision in `money.json`. Every optional field: "" / -1 = not decided. */
export class AppMoneyItem {
    public Id: string = "";
    public At: string = "";
    /** `open`, `settled`, or "" (only a correction). */
    public State: string = "";
    public SettledOn: string = "";
    /** Minor units, or -1 = not corrected. */
    public Amount: number = -1;
    public Currency: string = "";
    public Direction: string = "";
    public Due: string = "";
    public Add: AppMoneyAdd | null = null;
}

export class AppMoneyDoc {
    public Unreadable: boolean = false;
    public PersonKey: string = "";
    public Items: AppMoneyItem[] = [];

    /** The decision for `Id`, or null. DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Find = (Id: string): AppMoneyItem | null => {
        for (const One of this.Items) { if (One.Id === Id) { return One; } }
        return null;
    };
}

/** A correction asked for by the page. -1 / "" = leave as it is. */
export class AppMoneySet {
    public Amount: number = -1;
    public Currency: string = "";
    public Direction: string = "";
    public Due: string = "";
}

/** One money row as `index.json` (money ON) and the `/money` answer carry it. */
export class AppMoneyRow {
    public Id: string = "";
    /** `ai` (harvested) or `you` (added by the owner). */
    public Source: string = "";
    public State: string = "";
    public SettledOn: string = "";
    public DecidedIso: string = "";
    /** The owner changed amount, currency, direction or due date. */
    public Corrected: boolean = false;
    /** The paired task (`t-`) when the harvested block was also a task - settling one closes the other. */
    public TaskId: string = "";
    public Label: string = "";
    public Value: string = "";
    public Amount: number = 0;
    public Currency: string = "";
    public Direction: string = "";
    public Due: string = "";
    public Date: string = "";
    public Time: string = "";
    public Anchor: string = "";
}

export class AppMoneyAnswer {
    public Ok: boolean = false;
    public Row: AppMoneyRow | null = null;
    public Why: string = "";
    public Changed: boolean = true;
    public RetrySec: number = 0;
}

export class AppMoney {
    public static readonly Route: string = "/money";
    public static readonly FileName: string = "money.json";
    public static readonly Schema: number = 1;
    /** ⛔ Must match `MoneyRules` - pinned by `MoneyContract.test.ts`. */
    public static readonly Actions: string[] = ["settle", "reopen", "correct", "add"];
    public static readonly States: string[] = ["open", "settled"];
    public static readonly Directions: string[] = ["in", "out"];
    public static readonly Sources: string[] = ["ai", "you"];
    public static readonly AiPrefix: string = "m-";
    public static readonly UserPrefix: string = "v-";
    public static readonly AiIdPattern: RegExp = /^m-[0-9a-f]{12}$/;
    public static readonly UserIdPattern: RegExp = /^v-[0-9a-f]{12}$/;
    public static readonly CurrencyPattern: RegExp = /^[A-Z]{3}$/;
    public static readonly MaxLabel: number = 200;
    public static readonly MaxValue: number = 500;
    /** 10^12 minor units - nobody's chat holds more; a bigger number is a typo. */
    public static readonly MaxAmount: number = 1000000000000;
    public static readonly MaxItems: number = 500;
    /** The settled-or-not word the app uses for the Ask-about-everyone chip (`AppMoney.AskPreset`). */
    public static readonly AskPreset: string = "Who owes me money, and what do I owe?";

    /** Currencies with no minor unit and with three (ISO 4217). Everything else has two. */
    private static readonly Zero: string[] = ["BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG", "RWF",
        "UGX", "UYI", "VND", "VUV", "XAF", "XOF", "XPF"];
    private static readonly Three: string[] = ["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"];

    /** DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer. */
    public static IdOk = (Id: string): boolean => {
        return AppMoney.AiIdPattern.test(nz(Id)) === true || AppMoney.UserIdPattern.test(nz(Id)) === true;
    };

    /** A real `YYYY-MM-DD`. DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer. */
    public static DateOk = (Text: string): boolean => {
        return AppTaskApi.DateOk(Text);
    };

    /** DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer. */
    public static AmountOk = (N: unknown): boolean => {
        if (typeof N !== "number") { return false; }
        if (Number.isInteger(N) === false) { return false; }
        return (N as number) > 0 && (N as number) <= AppMoney.MaxAmount;
    };

    /** Minor-unit digits of a currency. DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Decimals = (Currency: string): number => {
        if (AppMoney.Zero.indexOf(nz(Currency)) >= 0) { return 0; }
        if (AppMoney.Three.indexOf(nz(Currency)) >= 0) { return 3; }
        return 2;
    };

    /** Minor units -> a plain number of whole units. DOCUMENTED EXCEPTION: pure arithmetic. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Major = (Minor: number, Currency: string): number => {
        return Minor / Math.pow(10, AppMoney.Decimals(Currency));
    };

    /**
     * What the owner typed ("1 250,50", "1250.5") -> minor units, or -1. ⚠ The LAST "," or "." with 1-3 digits after
     * it is the decimal mark; every other space, "'" or separator is grouping.
     * DOCUMENTED EXCEPTION: pure parse. ERROR-SUPPRESSED-JUSTIFIED: -1 (refused) IS the safe answer.
     */
    public static MinorOf = (Typed: string, Currency: string): number => {
        try {
            let T: string = nz(Typed).replace(/[\s']/g, "");
            if (/^[0-9.,]+$/.test(T) === false) { return -1; }
            const Last: number = Math.max(T.lastIndexOf(","), T.lastIndexOf("."));
            let Whole: string = T;
            let Frac: string = "";
            if (Last >= 0 && T.length - Last - 1 >= 1 && T.length - Last - 1 <= AppMoney.Decimals(Currency)) {
                Whole = T.substring(0, Last);
                Frac = T.substring(Last + 1);
            }
            Whole = Whole.replace(/[.,]/g, "");
            if (Whole.length === 0) { Whole = "0"; }
            T = Whole + (Frac + "000").substring(0, AppMoney.Decimals(Currency));
            const N: number = parseInt(T, 10);
            if (AppMoney.AmountOk(N) === false) { return -1; }
            return N;
        } catch (e) {
            return -1;
        }
    };

    // ── money.json ───────────────────────────────────────────────────────────────────────────────

    /** The strict reader. "" = no file yet (an empty, readable document). */
    public static Parse = (Text: string): AppMoneyDoc => {
        const Out: AppMoneyDoc = new AppMoneyDoc();
        if (getCoreUtils().KErrorMode === true) { Out.Unreadable = true; return Out; }
        try {
            if (nz(Text).trim().length === 0) { return Out; }
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { Out.Unreadable = true; return Out; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (Doc.schema !== AppMoney.Schema || Array.isArray(Doc.items) === false) { Out.Unreadable = true; return Out; }
            if (Doc.personKey !== undefined && typeof Doc.personKey !== "string") { Out.Unreadable = true; return Out; }
            Out.PersonKey = nz(Doc.personKey as string);
            const Seen: Record<string, boolean> = {};
            for (const One of Doc.items as unknown[]) {
                const Item: AppMoneyItem | null = AppMoney.ItemIn(One);
                if (Item === null || Seen[Item.Id] === true) { Out.Unreadable = true; Out.Items = []; return Out; }
                Seen[Item.Id] = true;
                Out.Items.push(Item);
            }
            if (Out.Items.length > AppMoney.MaxItems) { Out.Unreadable = true; Out.Items = []; }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a file in the subscriber's Dropbox that is not JSON is an EXPECTED
            // condition (fate 1): unreadable, never written over, said by the caller.
            Out.Unreadable = true;
            Out.Items = [];
            return Out;
        }
    };

    /** One item, or null when ANY field is not the contract's. */
    public static ItemIn = (Value: unknown): AppMoneyItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return null; }
            const Bag: Record<string, unknown> = Value as Record<string, unknown>;
            const Out: AppMoneyItem = new AppMoneyItem();
            if (typeof Bag.id !== "string" || AppMoney.IdOk(Bag.id) === false) { return null; }
            if (typeof Bag.at !== "string" || isNaN(Date.parse(Bag.at)) === true) { return null; }
            Out.Id = Bag.id;
            Out.At = Bag.at;
            if (Bag.state !== undefined) {
                if (typeof Bag.state !== "string" || AppMoney.States.indexOf(Bag.state) < 0) { return null; }
                Out.State = Bag.state;
            }
            if (Bag.settledOn !== undefined) {
                if (typeof Bag.settledOn !== "string" || AppMoney.DateOk(Bag.settledOn) === false || Out.State !== "settled") { return null; }
                Out.SettledOn = Bag.settledOn;
            }
            if (Bag.amount !== undefined) {
                if (AppMoney.AmountOk(Bag.amount) === false) { return null; }
                Out.Amount = Bag.amount as number;
            }
            if (Bag.currency !== undefined) {
                if (typeof Bag.currency !== "string" || AppMoney.CurrencyPattern.test(Bag.currency) === false) { return null; }
                Out.Currency = Bag.currency;
            }
            if (Bag.direction !== undefined) {
                if (typeof Bag.direction !== "string" || AppMoney.Directions.indexOf(Bag.direction) < 0) { return null; }
                Out.Direction = Bag.direction;
            }
            if (Bag.due !== undefined) {
                if (typeof Bag.due !== "string" || (Bag.due.length > 0 && AppMoney.DateOk(Bag.due) === false)) { return null; }
                Out.Due = Bag.due;
            }
            if (Bag.add !== undefined) {
                if (AppMoney.UserIdPattern.test(Out.Id) === false) { return null; }
                Out.Add = AppMoney.AddIn(Bag.add);
                if (Out.Add === null) { return null; }
            } else if (AppMoney.UserIdPattern.test(Out.Id) === true) { return null; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.ItemIn");
            return null;
        }
    };

    /** An item of the owner's own, or null when any field is not usable. */
    public static AddIn = (Value: unknown): AppMoneyAdd | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppMoney.AddWhy(Value).length > 0) { return null; }
            const Bag: Record<string, unknown> = Value as Record<string, unknown>;
            const Out: AppMoneyAdd = new AppMoneyAdd();
            Out.Label = nz(Bag.label as string).trim();
            Out.Value = StringOnlyOf(Bag.value).trim();
            Out.Amount = Bag.amount as number;
            Out.Currency = Bag.currency as string;
            Out.Direction = Bag.direction as string;
            Out.Due = StringOnlyOf(Bag.due).trim();
            Out.Date = StringOnlyOf(Bag.date).trim();
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.AddIn");
            return null;
        }
    };

    /** "" when an item of the owner's own is usable, else the sentence naming what to fix. */
    public static AddWhy = (Value: unknown): string => {
        if (getCoreUtils().KErrorMode === true) { return AppMoney.TypeLabelWhy(); }
        try {
            if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return AppMoney.TypeLabelWhy(); }
            const Bag: Record<string, unknown> = Value as Record<string, unknown>;
            if (typeof Bag.label !== "string" || Bag.label.trim().length === 0) { return AppMoney.TypeLabelWhy(); }
            if (Bag.label.trim().length > AppMoney.MaxLabel) { return "Keep the description under " + String(AppMoney.MaxLabel) + " characters."; }
            if (Bag.value !== undefined && (typeof Bag.value !== "string" || Bag.value.trim().length > AppMoney.MaxValue)) {
                return "Keep the note under " + String(AppMoney.MaxValue) + " characters.";
            }
            const SetWhy: string = AppMoney.SetWhy(Bag, true);
            if (SetWhy.length > 0) { return SetWhy; }
            if (Bag.date !== undefined && (typeof Bag.date !== "string" || (Bag.date.length > 0 && AppMoney.DateOk(Bag.date) === false))) {
                return "Choose a real date for when it arose.";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.AddWhy");
            return AppMoney.TypeLabelWhy();
        }
    };

    /** "" when a correction (or the money part of an add, `All` = every field required) is usable. */
    public static SetWhy = (Bag: Record<string, unknown>, All: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return AppMoney.AmountWhy(); }
        try {
            if (Bag.amount !== undefined || All === true) { if (AppMoney.AmountOk(Bag.amount) === false) { return AppMoney.AmountWhy(); } }
            if (Bag.currency !== undefined || All === true) {
                if (typeof Bag.currency !== "string" || AppMoney.CurrencyPattern.test(Bag.currency) === false) {
                    return "Choose the currency as its three-letter code, like EUR or SEK.";
                }
            }
            if (Bag.direction !== undefined || All === true) {
                if (typeof Bag.direction !== "string" || AppMoney.Directions.indexOf(Bag.direction) < 0) { return "Choose who owes whom."; }
            }
            if (Bag.due !== undefined && (typeof Bag.due !== "string" || (Bag.due.length > 0 && AppMoney.DateOk(Bag.due) === false))) {
                return "Choose a real date for when it is due.";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.SetWhy");
            return AppMoney.AmountWhy();
        }
    };

    /** The one serialiser. "" when it could not be written. */
    public static Json = (Doc: AppMoneyDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Items: Record<string, unknown>[] = [];
            for (const One of Doc.Items) {
                const Out: Record<string, unknown> = { id: One.Id, at: One.At };
                if (One.State.length > 0) { Out.state = One.State; }
                if (One.SettledOn.length > 0) { Out.settledOn = One.SettledOn; }
                if (One.Amount > 0) { Out.amount = One.Amount; }
                if (One.Currency.length > 0) { Out.currency = One.Currency; }
                if (One.Direction.length > 0) { Out.direction = One.Direction; }
                if (One.Due.length > 0) { Out.due = One.Due; }
                if (One.Add !== null) {
                    const A: AppMoneyAdd = One.Add as AppMoneyAdd;
                    Out.add = { label: A.Label, value: A.Value, amount: A.Amount, currency: A.Currency,
                        direction: A.Direction, due: A.Due, date: A.Date };
                }
                Items.push(Out);
            }
            return JSON.stringify({ schema: AppMoney.Schema, personKey: Doc.PersonKey, items: Items }, null, 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.Json");
            return "";
        }
    };

    // ── The wire ─────────────────────────────────────────────────────────────────────────────────

    /** The body of one action, or "" when it cannot be sent. */
    public static Body = (Phone: string, Key: string, Page: string, Action: string, Id: string,
                          Set: AppMoneySet | null, Add: AppMoneyAdd | null, SettledOn: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: Record<string, unknown> | null = AppTaskApi.ProofOf(Phone, Key, Page, AppMoney.Actions, Action, Id);
            if (Out === null) { return ""; }
            if (Action === "settle" && nz(SettledOn).length > 0) { Out.settledOn = nz(SettledOn); }
            if (Action === "correct" && Set !== null) {
                const S: AppMoneySet = Set as AppMoneySet;
                const Bag: Record<string, unknown> = {};
                if (S.Amount > 0) { Bag.amount = S.Amount; }
                if (S.Currency.length > 0) { Bag.currency = S.Currency; }
                if (S.Direction.length > 0) { Bag.direction = S.Direction; }
                if (S.Due.length > 0) { Bag.due = S.Due; }
                Out.set = Bag;
            }
            if (Action === "add" && Add !== null) {
                const A: AppMoneyAdd = Add as AppMoneyAdd;
                Out.add = { label: nz(A.Label).trim(), value: nz(A.Value).trim(), amount: A.Amount, currency: A.Currency,
                    direction: A.Direction, due: nz(A.Due), date: nz(A.Date) };
            }
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.Body");
            return "";
        }
    };

    /** Read an answer. ⛔ No boolean `ok` = a service without the route (`NotYetWhy`). */
    public static ParseAnswer = (Status: number, Text: string, OursWhy: string): AppMoneyAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppMoney.No(OursWhy); }
        try {
            const Read: AppAnswerDoc = AppAssistApi.ReadAnswerDoc(Status, Text);
            const Doc: Record<string, unknown> | null = Read.Doc;
            if (Doc === null) { return AppMoney.No(OursWhy); }
            if (Read.HasOk === false) { return AppMoney.No(AppMoney.NotYetWhy()); }
            if (Doc.ok !== true) {
                let Why: string = StringOnlyOf(Doc.why);
                if (Why.length === 0) { Why = OursWhy; }
                const Refused: AppMoneyAnswer = AppMoney.No(Why);
                Refused.RetrySec = AppAssistApi.RetryOf(Doc.retry);
                return Refused;
            }
            const Row: AppMoneyRow | null = AppMoney.RowIn(Doc.item);
            if (Row === null) { return AppMoney.No(OursWhy); }
            const Out: AppMoneyAnswer = new AppMoneyAnswer();
            Out.Ok = true;
            Out.Row = Row;
            Out.Changed = Doc.changed !== false;
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable body (a proxy's error page) is our end - `OursWhy` says so.
            return AppMoney.No(OursWhy);
        }
    };

    /** One row (an `index.json` money row with money ON, or an answer's `item`), or null. */
    public static RowIn = (Value: unknown): AppMoneyRow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return null; }
            const Bag: Record<string, unknown> = Value as Record<string, unknown>;
            const Out: AppMoneyRow = new AppMoneyRow();
            Out.Id = StringOnlyOf(Bag.id);
            Out.State = StringOnlyOf(Bag.state);
            if (AppMoney.IdOk(Out.Id) === false || AppMoney.States.indexOf(Out.State) < 0) { return null; }
            Out.Source = StringOnlyOf(Bag.source);
            Out.SettledOn = StringOnlyOf(Bag.settledOn);
            Out.DecidedIso = StringOnlyOf(Bag.decidedIso);
            Out.Corrected = Bag.corrected === true;
            Out.TaskId = StringOnlyOf(Bag.taskId);
            Out.Label = StringOnlyOf(Bag.label);
            Out.Value = StringOnlyOf(Bag.value);
            Out.Date = StringOnlyOf(Bag.date);
            Out.Time = StringOnlyOf(Bag.time);
            Out.Anchor = StringOnlyOf(Bag.anchor);
            const M: unknown = Bag.money;
            if (M !== null && typeof M === "object" && Array.isArray(M) === false) {
                const Money: Record<string, unknown> = M as Record<string, unknown>;
                if (typeof Money.amount === "number" && isFinite(Money.amount) === true) { Out.Amount = Money.amount; }
                Out.Currency = StringOnlyOf(Money.currency);
                Out.Direction = StringOnlyOf(Money.direction);
                Out.Due = StringOnlyOf(Money.due);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoney.RowIn");
            return null;
        }
    };

    /** ⭐ THE APP'S SWITCH. DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false (old view) IS the safe answer. */
    public static HasState = (Value: unknown): boolean => {
        if (Value === null || typeof Value !== "object" || Array.isArray(Value) === true) { return false; }
        return typeof (Value as Record<string, unknown>).state === "string" && typeof (Value as Record<string, unknown>).id === "string";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence. */
    public static NotYetWhy = (): string => {
        return "Revenue changes aren't available yet.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence. */
    public static TypeLabelWhy = (): string => {
        return "Type what the money is for.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence. */
    public static AmountWhy = (): string => {
        return "Type the amount as a number above zero.";
    };

    /** DOCUMENTED EXCEPTION: builds a value; cannot throw. */
    private static No = (Why: string): AppMoneyAnswer => {
        const Out: AppMoneyAnswer = new AppMoneyAnswer();
        Out.Why = nz(Why);
        return Out;
    };
}
